import { FormApiInterface } from 'react-uforms';
import { Question, QuestionRequired, questionSpecialIdentifiers } from '@ch/common/types/data';
import { customFieldAttr } from '../const';
import _ from 'lodash';

export const isSpecialQuestion = (question: Question) =>
  (Object.values(questionSpecialIdentifiers) as string[]).includes(question.identifier);

export const composeQuestions = (questions: Question[], api: FormApiInterface) => {
  const setRequiredState = (question: Question) => {
    const customAttr = api.getValue(`${customFieldAttr}.${question.identifier}`);
    const required =
      customAttr && Object.values(customAttr).some(value => value) ? QuestionRequired.NO : question.required;
    return { ...question, required };
  };

  const hasRelevantEventPersona = (question: Question) => {
    if (isSpecialQuestion(question)) {
      return true;
    }

    const hasMatchingPersonaAndEvent =
      question.personas?.includes(api.getValue(questionSpecialIdentifiers.PERSON_TYPE)) &&
      question.events?.includes(api.getValue(questionSpecialIdentifiers.FLOW));

    if (!hasMatchingPersonaAndEvent) {
      return false;
    }
    return true;
  };

  const applyConditions = (question: Question) => {
    if (!api || !question.conditions) {
      return question;
    }
    return question.conditions?.reduce(
      (acc, condition) => {
        const hasMatchingPersonaCondition = condition.persona
          ? condition.persona === api.getValue(questionSpecialIdentifiers.PERSON_TYPE)
          : true;

        const hasMatchingEventCondition = condition.event
          ? condition.event === api.getValue(questionSpecialIdentifiers.FLOW)
          : true;

        const isActiveCondition =
          condition.parent.length ===
          condition.parent.filter(({ identifier, value: conditionValue, isEmpty }) => {
            const apiValue = api.getValue(identifier);

            const isAnyValue = !conditionValue;
            const isArray = Array.isArray(apiValue);

            if (isEmpty) {
              return isArray ? apiValue.length === 0 : !apiValue;
            } else if (isAnyValue) {
              return isArray ? apiValue.length > 0 : !!apiValue;
            } else {
              return isArray ? apiValue.includes(conditionValue) : apiValue === conditionValue;
            }
          }).length;

        if (hasMatchingPersonaCondition && hasMatchingEventCondition && isActiveCondition) {
          acc[condition.attribute as string] = condition.value;
        }

        return acc;
      },
      { ...question },
    );
  };

  return _(questions)
    .filter(Boolean)
    .map(setRequiredState)
    .filter(hasRelevantEventPersona)
    .map(applyConditions)
    .value();
};
